import * as React from 'react'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { parseDateValue } from '@/modules/shared/utils/sortValues'
import { Tabs } from '@/modules/drivers/tabs/businessTrips/components/edit/Tabs'
import { ActiveTab } from '@/app/constants/tab'
import {
  FaIcon,
  faXmark,
  faShieldCheck,
  faShieldXmark,
  faShieldExclamation,
  faCircleCheck,
  faCircleXmark,
  faCircleExclamation,
  faClock,
  faChartPieSimple,
  faTable,
  faDownloadSolid,
} from '@/lib/fa'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip as ChartTooltip,
  Filler,
} from 'chart.js'
import jsPDF from 'jspdf'
import { toPng } from 'html-to-image'
ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, ChartTooltip, Filler)
import { TableSortIcon } from '@/modules/drivers/shared/tableItems/TableSortIcon'
import type { SortDirection } from '@/modules/drivers/types'
import { CertificateStatus } from '@/app/constants/import/certificateStatus'
import { ReadStatus } from '@/app/constants/import/readStatus'
import { Source } from '@/app/constants/import/source'
import type { FileDetailsDto, SpeedPointDto } from '@/lib/api/company/fileProcessing/fileDetails/types'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Props = {
  fileName: string
  details: FileDetailsDto | null
  onClose: () => void
}

function fmt(v: any, opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }): string {
  if (!v) return '—'
  const d = new Date(v)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('pl-PL', opts)
}

function fmtDt(v: any): string {
  return fmt(v, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

const ReadStatusBadge: React.FC<{ status: ReadStatus }> = ({ status }) => {
  const { t } = useLocalize()
  switch (status) {
    case ReadStatus.Complete:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-success tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-success"><FaIcon icon={faCircleCheck} />{t('importModule.read-complete')}</span>
    case ReadStatus.Warning:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-warning tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-warning"><FaIcon icon={faCircleExclamation} />{t('common.warning')}</span>
    case ReadStatus.Error:
    case ReadStatus.Terminated:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-error tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-error"><FaIcon icon={faCircleXmark} />{status === ReadStatus.Terminated ? t('importModule.read-terminated') : t('common.error')}</span>
    default:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-base-200 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-neutral"><FaIcon icon={faClock} />{t('importModule.unknown')}</span>
  }
}

const CertStatusBadge: React.FC<{ status: CertificateStatus }> = ({ status }) => {
  const { t } = useLocalize()
  switch (status) {
    case CertificateStatus.Valid:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-success tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-success"><FaIcon icon={faShieldCheck} />{t('importModule.cert-short-valid')}</span>
    case CertificateStatus.Invalid:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-content-error tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-error"><FaIcon icon={faShieldXmark} />{t('importModule.cert-short-invalid')}</span>
    default:
      return <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-base-200 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium tw-text-neutral"><FaIcon icon={faShieldExclamation} />{t('importModule.unknown')}</span>
  }
}

function useSortConfig() {
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: SortDirection } | null>(null)

  const handleSort = React.useCallback((key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }, [])

  // Gettery zwracaja albo tekst, albo liczbe (znacznik czasu, predkosc) - kolumny
  // dat i liczb musza isc porownaniem liczbowym, bo "28.10.2025" sortuje sie po dniu.
  const sortItems = React.useCallback(
    <T,>(items: T[], getters: Record<string, (item: T) => string | number>): T[] => {
      if (!sortConfig || !getters[sortConfig.key]) return items
      const get = getters[sortConfig.key]
      return [...items].sort((a, b) => {
        const av = get(a), bv = get(b)
        const result =
          typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av).localeCompare(String(bv), 'pl')
        return sortConfig.direction === 'asc' ? result : -result
      })
    },
    [sortConfig],
  )

  return { sortConfig, handleSort, sortItems }
}

const SortableTh: React.FC<{
  label: string
  sortKey: string
  sortConfig: { key: string; direction: SortDirection } | null
  onSort: (key: string) => void
}> = ({ label, sortKey, sortConfig, onSort }) => {
  const [hover, setHover] = React.useState(false)
  const direction = sortConfig?.key === sortKey ? sortConfig.direction : null
  return (
    <th
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => onSort(sortKey)}
      className="tw-relative tw-min-w-fit tw-cursor-pointer tw-text-nowrap tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-left tw-text-xs tw-text-content-base"
    >
      <div className="tw-flex tw-items-center tw-gap-1">
        <p className="tw-font-medium tw-uppercase">{label}</p>
        <TableSortIcon hover={hover} sortDirection={direction} />
      </div>
    </th>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="tw-text-sm">
      <label className="tw-mb-0 tw-py-0.5 tw-font-medium">{label}</label>
      <div>{value || '—'}</div>
    </div>
  )
}

const TabImport: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { t } = useLocalize()
  return (
  <div className="tw-p-4 tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
    <Row label={t('importModule.fi-read-status')} value={<ReadStatusBadge status={data.readStatus} />} />
    <Row label={t('importModule.fi-certificate')} value={<CertStatusBadge status={data.certificateStatus} />} />
    {data.certificateValidityDate && <Row label={t('importModule.fi-cert-validity-date')} value={fmt(data.certificateValidityDate)} />}
    {data.isCertificateValidityDateMismatch && (
      <div className="sm:tw-col-span-2 tw-rounded tw-bg-content-warning tw-border tw-border-warning/30 tw-px-3 tw-py-2 tw-text-xs tw-text-content-base">
        {t('importModule.fi-cert-date-mismatch')}
      </div>
    )}
    <Row label={t('importModule.fi-card-valid-from')} value={fmt(data.cardValidFrom)} />
    <Row label={t('importModule.fi-card-expiry')} value={fmt(data.cardExpirationDate)} />
    <Row label={t('importModule.fi-first-activity')} value={fmt(data.firstActivityTime)} />
    <Row label={t('importModule.fi-last-activity')} value={fmt(data.lastActivityTime)} />
    <Row label={t('importModule.fi-download-time')} value={fmtDt(data.downloadTime)} />
    <Row label={t('importModule.fi-file-name')} value={data.fileName} />
    {data.error && <Row label={t('importModule.fi-error-desc')} value={<span className="tw-text-error">{data.error}</span>} />}
    {data.tachographDetails && (
      <>
        <div className="sm:tw-col-span-2 tw-h-[1px] tw-w-full tw-bg-base-300" />
        <div className="sm:tw-col-span-2 tw-text-sm tw-font-medium">{t('importModule.fi-tachograph-data')}</div>
        <Row label={t('importModule.fi-tacho-manufacturer')} value={data.tachographDetails.manufacturerName} />
        <Row label={t('importModule.fi-tacho-address')} value={data.tachographDetails.manufacturerAddress} />
        <Row label={t('importModule.fi-tacho-code')} value={data.tachographDetails.manufacturerCode} />
        <Row label={t('importModule.fi-tacho-serial')} value={data.tachographDetails.serialNumber} />
        <Row label={t('importModule.fi-tacho-part')} value={data.tachographDetails.partNumber} />
        <Row label={t('importModule.fi-tacho-software')} value={data.tachographDetails.softwareVersion} />
        <Row label={t('importModule.fi-tacho-approval')} value={data.tachographDetails.approvalNumber} />
        <Row label={t('importModule.fi-tacho-mfg-date')} value={fmt(data.tachographDetails.manufacturingDate)} />
        <Row label={t('importModule.fi-tacho-generation')} value={data.tachographDetails.generation} />
      </>
    )}
  </div>
  )
}

const TabLocks: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { sortConfig, handleSort, sortItems } = useSortConfig()
  const rows = sortItems(data.companyLocks ?? [], {
    start: (l) => parseDateValue(l.lockStartDate),
    end: (l) => parseDateValue(l.lockEndDate),
    company: (l) => l.companyName ?? '',
    card: (l) => l.lockingCardNumber ?? '',
  })
  const { t } = useLocalize()
  if (!data.companyLocks?.length) return <EmptyState label={t('importModule.fi-no-locks')} />
  return (
    <div className="tw-overflow-auto">
      <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
        <thead className="notches-border--right tw-select-none tw-border-b tw-bg-content-primary tw-sticky tw-top-0 tw-z-10">
          <tr>
            <SortableTh label={t('importModule.fi-lock-start')} sortKey="start" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-lock-end')} sortKey="end" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-lock-company')} sortKey="company" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-card-number')} sortKey="card" sortConfig={sortConfig} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((l, i) => (
            <tr key={i} className={`notches-border--right tw-border-b tw-bg-white hover:tw-bg-primary/10 ${l.isActive ? 'tw-font-semibold' : ''}`}>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmt(l.lockStartDate)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmt(l.lockEndDate)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{l.companyName ?? '—'}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{l.lockingCardNumber ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TabCalibrations: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { sortConfig, handleSort, sortItems } = useSortConfig()
  const rows = sortItems(data.calibrations ?? [], {
    date: (c) => parseDateValue(c.calibrationDate),
    next: (c) => parseDateValue(c.nextCalibrationDate),
    workshop: (c) => c.workshopName ?? '',
    address: (c) => c.workshopAddress ?? '',
  })
  const { t } = useLocalize()
  if (!data.calibrations?.length) return <EmptyState label={t('importModule.fi-no-calibrations')} />
  return (
    <div className="tw-overflow-auto">
      <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
        <thead className="notches-border--right tw-select-none tw-border-b tw-bg-content-primary tw-sticky tw-top-0 tw-z-10">
          <tr>
            <SortableTh label={t('importModule.fi-calib-date')} sortKey="date" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-calib-next')} sortKey="next" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-calib-workshop')} sortKey="workshop" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-calib-address')} sortKey="address" sortConfig={sortConfig} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} className="notches-border--right tw-border-b tw-bg-white hover:tw-bg-primary/10">
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmt(c.calibrationDate)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmt(c.nextCalibrationDate)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{c.workshopName ?? '—'}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{c.workshopAddress ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const FAULT_TYPE_KEYS: Record<string, string> = {
  'General events - Card conflict': 'importModule.fault-card-conflict',
  'General events - Card insertion while driving': 'importModule.fault-card-insertion-driving',
  'General events - Driving without an appropriate card': 'importModule.fault-driving-no-card',
  'General events - Insertion of a non-valid card': 'importModule.fault-invalid-card',
  'Insertion of a non-valid card': 'importModule.fault-invalid-card',
  'General events - Last card session not correctly closed': 'importModule.fault-session-not-closed',
  'General events - Motion data error': 'importModule.fault-motion-data-error',
  'General events - Power supply interruption': 'importModule.fault-power-supply',
  'General events - Time overlap': 'importModule.fault-time-overlap',
  'General events - Over speeding': 'importModule.fault-over-speeding',
  'Sensor related security breach attempt events - Internal data transfer error': 'importModule.fault-sensor-transfer-error',
  'Vehicle unit related security breach attempt events - Card data input integrity error': 'importModule.fault-card-integrity',
  'Vehicle unit related security breach attempt events - Hardware sabotage': 'importModule.fault-hardware-sabotage',
  'Vehicle unit related security breach attempt events - Internal data transfer error': 'importModule.fault-internal-transfer',
  'Vehicle unit related security breach attempt events - Motion sensor authentication failure': 'importModule.fault-sensor-auth',
  'Vehicle unit related security breach attempt events - Stored user data integrity error': 'importModule.fault-data-integrity',
  'Vehicle unit related security breach attempt events - Tachograph card authentication failure': 'importModule.fault-card-auth',
  'Vehicle unit related security breach attempt events - Unauthorised case opening': 'importModule.fault-case-opening',
  'Vehicle unit related security breach attempt events - Unauthorised change of motion sensor': 'importModule.fault-sensor-change',
  'Card faults - No further details': 'importModule.fault-card-no-details',
  'Recording equipment faults - Downloading fault': 'importModule.fault-download',
  'Recording equipment faults - No further details': 'importModule.fault-no-details',
  'Recording equipment faults - Printer fault': 'importModule.fault-printer',
  'Recording equipment faults - Sensor fault': 'importModule.fault-sensor',
  'Recording equipment faults - VU internal fault': 'importModule.fault-vu-internal',
  'Manufacturer specific': 'importModule.fault-manufacturer',
}

function translateFaultType(faultType: string | null, t: (key: string) => string): string {
  if (!faultType) return '—'
  const key = FAULT_TYPE_KEYS[faultType]
  return key ? t(key) : faultType
}

const TabFaults: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { sortConfig, handleSort, sortItems } = useSortConfig()
  const { t } = useLocalize()
  const rows = sortItems(data.faults ?? [], {
    begin: (f) => parseDateValue(f.faultBegin),
    end: (f) => parseDateValue(f.faultEnd),
    type: (f) => translateFaultType(f.faultType, t),
  })
  if (!data.faults?.length) return <EmptyState label={t('importModule.fi-no-faults')} />
  return (
    <div className="tw-overflow-auto">
      <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
        <thead className="notches-border--right tw-select-none tw-border-b tw-bg-content-primary tw-sticky tw-top-0 tw-z-10">
          <tr>
            <SortableTh label={t('importModule.fi-fault-begin')} sortKey="begin" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-fault-end')} sortKey="end" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-fault-type')} sortKey="type" sortConfig={sortConfig} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((f, i) => (
            <tr key={i} className="notches-border--right tw-border-b tw-bg-white hover:tw-bg-primary/10">
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmtDt(f.faultBegin)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmtDt(f.faultEnd)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{translateFaultType(f.faultType, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TabControls: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { sortConfig, handleSort, sortItems } = useSortConfig()
  const rows = sortItems(data.controls ?? [], {
    type: (c) => c.controlType ?? '',
    time: (c) => parseDateValue(c.controlTime),
    card: (c) => c.controlCardNumber ?? '',
    from: (c) => parseDateValue(c.downloadPeriodBegin),
    to: (c) => parseDateValue(c.downloadPeriodEnd),
  })
  const { t } = useLocalize()
  if (!data.controls?.length) return <EmptyState label={t('importModule.fi-no-controls')} />
  return (
    <div className="tw-overflow-auto">
      <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
        <thead className="notches-border--right tw-select-none tw-border-b tw-bg-content-primary tw-sticky tw-top-0 tw-z-10">
          <tr>
            <SortableTh label={t('importModule.fi-control-type')} sortKey="type" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-control-date')} sortKey="time" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-control-card')} sortKey="card" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-control-period-start')} sortKey="from" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-control-period-end')} sortKey="to" sortConfig={sortConfig} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((c, i) => (
            <tr key={i} className="notches-border--right tw-border-b tw-bg-white hover:tw-bg-primary/10">
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{c.controlType ?? '—'}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmtDt(c.controlTime)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{c.controlCardNumber ?? '—'}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmt(c.downloadPeriodBegin)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmt(c.downloadPeriodEnd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const SIGNATURE_BLOCK_KEYS: Record<string, string> = {
  DataOverview: 'importModule.sig-tachograph-data',
  DataOverviewSecondGen: 'importModule.sig-tachograph-data',
  DataOverviewSecondGenSecondVer: 'importModule.sig-tachograph-data',
  DataActivities: 'importModule.sig-vehicle-activities',
  DataActivitiesSecondGen: 'importModule.sig-vehicle-activities',
  DataActivitiesSecondGenSecondVer: 'importModule.sig-vehicle-activities',
  DataEventsAndFaults: 'importModule.sig-events-faults',
  DataEventsAndFaultsSecondGen: 'importModule.sig-events-faults',
  DataEventsAndFaultsSecondGenSecondVer: 'importModule.sig-events-faults',
  DataTechnicalData: 'importModule.sig-technical-data',
  DataTechnicalDataSecondGen: 'importModule.sig-technical-data',
  DataTechnicalDataSecondGenSecondVer: 'importModule.sig-technical-data',
  DataDetailedSpeed: 'importModule.sig-speeds',
  DataDetailedSpeedSecondGen: 'importModule.sig-speeds',
  DataDetailedSpeedSecondGenSecondVer: 'importModule.sig-speeds',
}

function signatureBlockName(raw: string | null, t: (key: string) => string): string {
  if (!raw) return '—'
  const key = SIGNATURE_BLOCK_KEYS[raw]
  return key ? t(key) : raw
}

const TabSignatures: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { sortConfig, handleSort, sortItems } = useSortConfig()
  const { t } = useLocalize()
  const rows = sortItems(data.signatures ?? [], {
    block: (s) => signatureBlockName(s.signatureBlockTypeName, t),
    date: (s) => parseDateValue(s.activityDateFormatted),
    valid: (s) => s.signatureCorrect ? t('common.yes') : t('common.no'),
  })
  if (!data.signatures?.length) return <EmptyState label={t('importModule.fi-no-signatures')} />
  return (
    <div className="tw-overflow-auto">
      <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
        <thead className="notches-border--right tw-select-none tw-border-b tw-bg-content-primary tw-sticky tw-top-0 tw-z-10">
          <tr>
            <SortableTh label={t('importModule.fi-sig-block')} sortKey="block" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-sig-date')} sortKey="date" sortConfig={sortConfig} onSort={handleSort} />
            <SortableTh label={t('importModule.fi-sig-valid')} sortKey="valid" sortConfig={sortConfig} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => (
            <tr key={i} className={`notches-border--right tw-border-b tw-bg-white hover:tw-bg-primary/10 ${!s.signatureCorrect ? 'tw-bg-content-error hover:tw-bg-content-error' : ''}`}>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{signatureBlockName(s.signatureBlockTypeName, t)}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{s.activityDateFormatted ?? '—'}</td>
              <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-opacity-90">
                {s.signatureCorrect
                  ? <span className="tw-text-success tw-font-medium">{t('common.yes')}</span>
                  : <span className="tw-text-error tw-font-medium">{t('common.no')}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TabWarnings: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { t } = useLocalize()
  if (!data.fileStructureWarnings?.length) return <EmptyState label={t('importModule.fi-no-warnings')} />
  return (
    <ol className="tw-p-4 tw-space-y-2 tw-list-none">
      {data.fileStructureWarnings.map((w, i) => (
        <li key={i} className="tw-flex tw-gap-3 tw-rounded-md tw-border tw-border-warning/30 tw-bg-content-warning tw-p-3">
          <span className="tw-inline-flex tw-h-6 tw-min-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-warning/10 tw-text-xs tw-font-semibold tw-text-warning">
            {i + 1}
          </span>
          <span className="tw-text-sm tw-text-content-base">{w}</span>
        </li>
      ))}
    </ol>
  )
}

function avgSpeed(speeds: number[]): number {
  return speeds.length ? Math.round(speeds.reduce((a, b) => a + b, 0) / speeds.length) : 0
}

function dayKey(isoMinute: string): string {
  return isoMinute.slice(0, 10) // 'YYYY-MM-DD' without timezone shift
}

function fmtDay(yyyyMmDd: string): string {
  // Parse as noon local time to avoid DST/UTC boundary issues
  return new Date(yyyyMmDd + 'T12:00:00').toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function fmtMonth(yyyyMm: string): string {
  return new Date(yyyyMm + '-15T12:00:00').toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })
}

/** All days YYYY-MM-DD for a given YYYY-MM month string */
function daysOfMonth(yyyyMm: string): string[] {
  const [y, m] = yyyyMm.split('-').map(Number)
  const count = new Date(y, m, 0).getDate()
  return Array.from({ length: count }, (_, i) => `${yyyyMm}-${String(i + 1).padStart(2, '0')}`)
}

function fmtTime(isoMinute: string): string {
  const d = new Date(isoMinute)
  return isNaN(d.getTime()) ? isoMinute : d.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })
}

const CHART_DATASET_BASE = {
  borderColor: '#3C7CFF',
  backgroundColor: 'rgba(60, 124, 255, 0.08)',
  borderWidth: 1.5,
  tension: 0.3,
  fill: true,
} as const

function makeChartOptions(maxTicks = 14): object {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { mode: 'index', intersect: false },
    },
    scales: {
      x: {
        ticks: { maxTicksLimit: maxTicks, font: { size: 10 }, maxRotation: 30 },
        grid: { display: false },
      },
      y: {
        title: { display: true, text: 'km/h', font: { size: 11 } },
        beginAtZero: true,
      },
    },
  }
}

type DaySpeedViewHandle = { getCanvas: () => HTMLCanvasElement | null }

const DaySpeedView = React.forwardRef<DaySpeedViewHandle, { rows: SpeedPointDto[] }>(({ rows }, ref) => {
  const { t } = useLocalize()
  const chartRef = React.useRef<ChartJS<'line'>>(null)
  React.useImperativeHandle(ref, () => ({ getCanvas: () => chartRef.current?.canvas ?? null }), [])
  const rowRefs = React.useRef<Map<number, HTMLTableRowElement>>(new Map())
  const tableScrollRef = React.useRef<HTMLDivElement>(null)
  const hoveredIndexRef = React.useRef<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = React.useState<number | null>(null)
  const [hoverSource, setHoverSource] = React.useState<'chart' | 'table' | null>(null)

  // Table row → chart: set active tooltip element
  React.useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    if (hoveredIndex === null) {
      chart.setActiveElements([])
      chart.tooltip?.setActiveElements([], { x: 0, y: 0 })
    } else {
      chart.setActiveElements([{ datasetIndex: 0, index: hoveredIndex }])
      chart.tooltip?.setActiveElements([{ datasetIndex: 0, index: hoveredIndex }], { x: 0, y: 0 })
    }
    chart.update('none')
  }, [hoveredIndex])

  // Chart hover → scroll table's own container to the matching row
  React.useEffect(() => {
    if (hoverSource === 'chart' && hoveredIndex !== null) {
      const rowEl = rowRefs.current.get(hoveredIndex)
      const container = tableScrollRef.current
      if (!rowEl || !container) return
      const rowTop = rowEl.offsetTop
      const rowBottom = rowTop + rowEl.offsetHeight
      const { scrollTop, clientHeight } = container
      if (rowTop < scrollTop) {
        container.scrollTop = rowTop
      } else if (rowBottom > scrollTop + clientHeight) {
        container.scrollTop = rowBottom - clientHeight
      }
    }
  }, [hoveredIndex, hoverSource])

  const chartData = React.useMemo(() => ({
    labels: rows.map(p => fmtTime(p.minute)),
    datasets: [{
      ...CHART_DATASET_BASE,
      label: t('importModule.fi-speed-kmh'),
      data: rows.map(p => avgSpeed(p.speed)),
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 10,
    }],
  }), [rows, t])

  const chartOptions = React.useMemo(() => ({
    ...makeChartOptions(16),
    onHover: (_: unknown, elements: Array<{ index: number }>) => {
      const newIdx = elements.length > 0 ? elements[0].index : null
      if (newIdx !== hoveredIndexRef.current) {
        hoveredIndexRef.current = newIdx
        setHoveredIndex(newIdx)
        setHoverSource(newIdx !== null ? 'chart' : null)
      }
    },
  }), [])

  return (
    <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col">
      {/* Chart — sticky so it stays visible while the table scrolls */}
      <div className="tw-sticky tw-top-0 tw-z-10 tw-flex-shrink-0 tw-border-b tw-border-base-300 tw-bg-base-100 tw-p-4" style={{ height: 260 }}>
        <Line ref={chartRef} data={chartData} options={chartOptions} />
      </div>

      {/* Table — own scroll container so chart-driven scrollIntoView doesn't move the modal */}
      <div ref={tableScrollRef} className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
        <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
          <thead className="notches-border--right tw-select-none tw-border-b tw-bg-content-primary tw-sticky tw-top-0 tw-z-[9]">
            <tr>
              <th className="tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-text-content-base">{t('common.time')}</th>
              <th className="tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-text-content-base">{t('importModule.fi-speeds-kmh')}</th>
              <th className="tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-text-content-base">{t('importModule.fi-avg-speed')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr
                key={i}
                ref={(el) => {
                  // React 19: callback refa zwraca void albo funkcję sprzątającą,
                  // a set()/delete() oddawały Mapę i boolean
                  if (el) rowRefs.current.set(i, el)
                  else rowRefs.current.delete(i)
                }}
                onMouseEnter={() => { setHoveredIndex(i); setHoverSource('table') }}
                onMouseLeave={() => { setHoveredIndex(null); setHoverSource(null) }}
                className={`notches-border--right tw-border-b tw-cursor-default tw-transition-colors ${
                  hoveredIndex === i
                    ? 'tw-z-10 tw-bg-primary/10 tw-ring-1 tw-ring-inset tw-ring-primary/40'
                    : 'tw-bg-white hover:tw-bg-primary/10'
                }`}
              >
                <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base tw-opacity-90">{fmtTime(p.minute)}</td>
                <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base tw-opacity-90 tw-max-w-[200px] tw-truncate">{p.speed.join(', ')}</td>
                <td className="tw-px-3 tw-py-2 tw-text-sm tw-text-content-base tw-opacity-90">{avgSpeed(p.speed)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
})

export const TabSpeed: React.FC<{ data: FileDetailsDto }> = ({ data }) => {
  const { t } = useLocalize()
  const speed = data.speedInfo
  const allRows = speed?.speedPerMinute ?? []

  const [view, setView] = React.useState<'month' | 'day' | 'all' | 'table'>('month')
  const { sortConfig, handleSort, sortItems } = useSortConfig()
  const chartRef = React.useRef<ChartJS<'line'>>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)
  const dayViewRef = React.useRef<DaySpeedViewHandle>(null)

  // Available months (YYYY-MM) derived from data
  const availableMonths = React.useMemo(() => {
    const months = new Set<string>()
    allRows.forEach(p => months.add(p.minute.slice(0, 7)))
    return Array.from(months).sort()
  }, [allRows])

  const [selectedMonth, setSelectedMonth] = React.useState<string>('')

  React.useEffect(() => {
    if (availableMonths.length > 0 && !availableMonths.includes(selectedMonth)) {
      setSelectedMonth(availableMonths[0])
    }
  }, [availableMonths, selectedMonth])

  // Available days for the day selector
  const availableDays = React.useMemo(() => {
    const days = new Set<string>()
    allRows.forEach(p => days.add(dayKey(p.minute)))
    return Array.from(days).sort()
  }, [allRows])

  const [selectedDay, setSelectedDay] = React.useState<string>('')

  React.useEffect(() => {
    if (availableDays.length > 0 && !availableDays.includes(selectedDay)) {
      setSelectedDay(availableDays[0])
    }
  }, [availableDays, selectedDay])

  // Monthly chart: one point per day, ALL days of the month shown (null = gap)
  const monthChartData = React.useMemo(() => {
    if (!selectedMonth) return { labels: [] as string[], datasets: [] }
    const byDay = new Map<string, number[]>()
    allRows
      .filter(p => p.minute.slice(0, 7) === selectedMonth)
      .forEach(p => {
        const k = dayKey(p.minute)
        byDay.set(k, (byDay.get(k) ?? []).concat(p.speed))
      })
    const days = daysOfMonth(selectedMonth)
    return {
      labels: days.map(d => fmtDay(d)),
      datasets: [{
        ...CHART_DATASET_BASE,
        label: t('importModule.fi-avg-speed-kmh'),
        data: days.map(d => byDay.has(d) ? avgSpeed(byDay.get(d)!) : null),
        pointRadius: 4,
        spanGaps: false,
      }],
    }
  }, [allRows, selectedMonth, t])

  // All-data chart: every minute across all records
  const allChartData = React.useMemo(() => ({
    labels: allRows.map(p => fmtDt(p.minute)),
    datasets: [{ ...CHART_DATASET_BASE, label: t('importModule.fi-avg-speed-kmh'), data: allRows.map(p => avgSpeed(p.speed)), pointRadius: 0 }],
  }), [allRows, t])

  const tableRows = sortItems(allRows.slice(0, 500), {
    minute: (p) => parseDateValue(p.minute),
    avg: (p) => avgSpeed(p.speed),
  })

  const [isExporting, setIsExporting] = React.useState(false)

  const handleExportPdf = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const reg = speed.vehicleRegistrationNumber ? ` — ${speed.vehicleRegistrationNumber}` : ''
      const title = view === 'month'
        ? `Predkosc — ${fmtMonth(selectedMonth)}${reg}`
        : view === 'day'
          ? `Predkosc — ${fmtDay(selectedDay)}${reg}`
          : view === 'all'
            ? `Predkosc — wszystko${reg}`
            : `Predkosc — tabela${reg}`

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
      const pageW = pdf.internal.pageSize.getWidth()
      const pageH = pdf.internal.pageSize.getHeight()
      const margin = 10

      let imgDataUrl: string
      if (view === 'table') {
        // Table: use html-to-image (supports oklch/modern CSS)
        if (!tableRef.current) return
        imgDataUrl = await toPng(tableRef.current, { backgroundColor: '#ffffff', pixelRatio: 2 })
      } else {
        // Charts: read canvas directly — fast and no CSS colour issues
        const canvas = view === 'day'
          ? dayViewRef.current?.getCanvas()
          : chartRef.current?.canvas
        if (!canvas) return
        imgDataUrl = canvas.toDataURL('image/png')
      }

      // Fit image to page, paginating if needed
      const img = new Image()
      await new Promise<void>(res => { img.onload = () => res(); img.src = imgDataUrl })
      const imgW = pageW - margin * 2
      const imgH = (img.naturalHeight / img.naturalWidth) * imgW
      let yOffset = margin
      let srcYRatio = 0
      let remaining = imgH
      while (remaining > 0) {
        const sliceH = Math.min(remaining, pageH - margin - yOffset)
        const sliceCanvas = document.createElement('canvas')
        sliceCanvas.width = img.naturalWidth
        sliceCanvas.height = Math.round((sliceH / imgW) * img.naturalWidth)
        const ctx = sliceCanvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(img, 0, srcYRatio * img.naturalHeight, img.naturalWidth, sliceCanvas.height, 0, 0, img.naturalWidth, sliceCanvas.height)
          pdf.addImage(sliceCanvas.toDataURL('image/png'), 'PNG', margin, yOffset, imgW, sliceH)
        }
        srcYRatio += sliceCanvas.height / img.naturalHeight
        remaining -= sliceH
        if (remaining > 0) { pdf.addPage(); yOffset = margin }
      }

      pdf.save(`${title.replace(/[^\w \-_]/g, '_')}.pdf`)
    } finally {
      setIsExporting(false)
    }
  }

  const segBtn = (v: typeof view, label: string, icon: any, border = true) => (
    <button type="button" onClick={() => setView(v)}
      className={`tw-flex tw-items-center tw-gap-1.5 ${border ? 'tw-border-l tw-border-base-300 ' : ''}tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-transition-colors ${view === v ? 'tw-bg-primary tw-text-white' : 'tw-bg-white tw-text-neutral hover:tw-bg-base-100'}`}>
      <FaIcon icon={icon} />{label}
    </button>
  )

  // Strażnik stał nad `useState` - po dojściu danych liczba hooków zmieniała się
  // między renderami.
  if (!speed || !allRows.length)
    return <EmptyState label={t('importModule.fi-no-speed-data')} />

  return (
    <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col">
      {/* Action bar */}
      <div className="tw-flex tw-flex-shrink-0 tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-2.5">
        <p className="tw-m-0 tw-text-sm tw-text-content-base">
          {speed.vehicleRegistrationNumber ? (
            <>
              {t('common.vehicle')}:{' '}
              <span className="tw-font-semibold">{speed.vehicleRegistrationNumber}</span>
            </>
          ) : (
            <span className="tw-text-neutral">&nbsp;</span>
          )}
        </p>
        <div className="tw-flex tw-items-center tw-gap-2">
          <div className="tw-flex tw-overflow-hidden tw-rounded-md tw-border tw-border-base-300">
            {segBtn('month', t('importModule.fi-month'), faChartPieSimple, false)}
            {segBtn('day', t('importModule.fi-day'), faChartPieSimple)}
            {segBtn('all', t('common.all'), faChartPieSimple)}
            {segBtn('table', t('importModule.fi-table'), faTable)}
          </div>
          <button type="button" onClick={handleExportPdf} disabled={isExporting}
            className="tw-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-neutral hover:tw-bg-base-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
            <FaIcon icon={faDownloadSolid} />{isExporting ? t('importModule.fi-generating') : t('importModule.fi-download-pdf')}
          </button>
        </div>
      </div>

      {/* Monthly chart — average speed per day, all days of month shown */}
      {view === 'month' && (
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
          <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
            <label className="tw-text-xs tw-font-medium tw-text-neutral">{t('importModule.fi-month')}:</label>
            <select
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
              className="tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-text-content-base focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary"
            >
              {availableMonths.map(m => (
                <option key={m} value={m}>{fmtMonth(m)}</option>
              ))}
            </select>
          </div>
          <div className="tw-min-h-[320px] tw-flex-1 tw-p-4">
            <Line ref={chartRef} data={monthChartData} options={makeChartOptions(daysOfMonth(selectedMonth || availableMonths[0] || '2000-01').length)} />
          </div>
        </div>
      )}

      {/* Daily view — day selector + chart + interactive table */}
      {view === 'day' && (
        <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
          <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-3 tw-border-b tw-border-base-300 tw-px-4 tw-py-2">
            <label className="tw-text-xs tw-font-medium tw-text-neutral">{t('importModule.fi-day')}:</label>
            <select
              value={selectedDay}
              onChange={e => setSelectedDay(e.target.value)}
              className="tw-rounded-md tw-border tw-border-base-300 tw-bg-white tw-px-2 tw-py-1 tw-text-xs tw-text-content-base focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-primary"
            >
              {availableDays.map(d => (
                <option key={d} value={d}>{fmtDay(d)}</option>
              ))}
            </select>
          </div>
          <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
            <DaySpeedView ref={dayViewRef} key={selectedDay} rows={allRows.filter(p => dayKey(p.minute) === selectedDay)} />
          </div>
        </div>
      )}

      {/* All-data chart — every minute across all records */}
      {view === 'all' && (
        <div className="tw-min-h-[340px] tw-flex-1 tw-p-4">
          <Line ref={chartRef} data={allChartData} options={makeChartOptions(14)} />
        </div>
      )}

      {/* Table view */}
      {view === 'table' && (
        <div className="tw-min-h-0 tw-flex-1 tw-overflow-auto">
          <table ref={tableRef} className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <thead className="notches-border--right tw-sticky tw-top-0 tw-z-10 tw-select-none tw-border-b tw-bg-content-primary">
              <tr>
                <SortableTh label={t('importModule.fi-time-minute')} sortKey="minute" sortConfig={sortConfig} onSort={handleSort} />
                <th className="tw-px-3 tw-pb-1.5 tw-pt-2.5 tw-text-left tw-text-xs tw-font-medium tw-uppercase tw-text-nowrap tw-text-content-base">{t('importModule.fi-speeds-kmh')}</th>
                <SortableTh label={t('importModule.fi-avg-speed')} sortKey="avg" sortConfig={sortConfig} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {tableRows.length === 0 ? (
                <tr><td colSpan={3}><EmptyState label={t('importModule.fi-no-results')} /></td></tr>
              ) : tableRows.map((p, i) => (
                <tr key={i} className="notches-border--right tw-border-b tw-bg-white hover:tw-bg-primary/10">
                  <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{fmtDt(p.minute)}</td>
                  <td className="tw-max-w-[200px] tw-truncate tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{p.speed.join(', ')}</td>
                  <td className="tw-px-3 tw-py-2.5 tw-text-sm tw-text-content-base tw-opacity-90">{avgSpeed(p.speed)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!sortConfig && allRows.length > 500 && (
            <p className="tw-p-3 tw-text-center tw-text-xs tw-text-neutral">
              {t('importModule.fi-showing-entries').replace('{0}', '500').replace('{1}', String(allRows.length))}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="tw-flex tw-h-32 tw-items-center tw-justify-center tw-text-sm tw-text-neutral">
    {label}
  </div>
)

type TabKey = 'import' | 'locks' | 'calibrations' | 'faults' | 'controls' | 'signatures' | 'warnings' | 'speed'

export const FileInfoModal: React.FC<Props> = ({ fileName, details, onClose }) => {
  const { t } = useLocalize()
  const [activeTab, setActiveTab] = React.useState<TabKey>('import')

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  const isDriverCard = details?.source === Source.Card

  const tabs = React.useMemo(() => {
    if (!details) return []
    const all = [
      { name: t('importModule.tab-import-details'), active: activeTab === 'import', type: ActiveTab.FILE_DETAILS_IMPORT },
      { name: t('importModule.tab-locks'), active: activeTab === 'locks', type: ActiveTab.FILE_DETAILS_LOCKS, stickers: details.companyLocks?.length ? [String(details.companyLocks.length)] : undefined, vehicleOnly: true },
      { name: t('importModule.tab-calibrations'), active: activeTab === 'calibrations', type: ActiveTab.FILE_DETAILS_CALIBRATIONS, stickers: details.calibrations?.length ? [String(details.calibrations.length)] : undefined, vehicleOnly: true },
      { name: t('importModule.tab-faults'), active: activeTab === 'faults', type: ActiveTab.FILE_DETAILS_FAULTS, stickers: details.faults?.length ? [String(details.faults.length)] : undefined, vehicleOnly: true },
      { name: t('importModule.tab-controls'), active: activeTab === 'controls', type: ActiveTab.FILE_DETAILS_CONTROLS, stickers: details.controls?.length ? [String(details.controls.length)] : undefined, vehicleOnly: true },
      { name: t('importModule.tab-signatures'), active: activeTab === 'signatures', type: ActiveTab.FILE_DETAILS_SIGNATURES, stickers: details.signatures?.length ? [String(details.signatures.length)] : undefined, invalid: details.signatures?.some(s => !s.signatureCorrect) },
      { name: t('importModule.tab-other-errors'), active: activeTab === 'warnings', type: ActiveTab.FILE_DETAILS_WARNINGS, stickers: details.fileStructureWarnings?.length ? [String(details.fileStructureWarnings.length)] : undefined, vehicleOnly: true },
      { name: t('importModule.tab-speed'), active: activeTab === 'speed', type: ActiveTab.FILE_DETAILS_SPEED, vehicleOnly: true },
    ]
    return isDriverCard ? all.filter(t => !t.vehicleOnly).map(({ vehicleOnly, ...rest }) => rest) : all.map(({ vehicleOnly, ...rest }) => rest)
  }, [details, activeTab, isDriverCard, t])

  return (
    <ModalLayout customZIndex={10000}>
      <div
        className="tw-fixed tw-inset-0"
        onClick={handleBackdropClick}
      >
        <div className="tw-absolute tw-left-1/2 tw-top-1/2 tw-w-[min(90vw,1200px)] tw-h-[85vh] tw--translate-x-1/2 tw--translate-y-1/2 tw-flex tw-flex-col tw-rounded-xl tw-bg-base-100 tw-shadow-2xl tw-ring-1 tw-ring-base-300 tw-font-primary">
          {/* Header */}
          <div className="tw-flex tw-flex-none tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-px-5 tw-py-3">
            <div>
              <p className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-neutral">{t('importModule.file-info')}</p>
              <p className="tw-text-sm tw-font-semibold tw-text-content-base tw-truncate tw-max-w-[600px]">{fileName}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-neutral hover:tw-bg-base-200 hover:tw-text-content-base"
            >
              <FaIcon icon={faXmark} className="tw-h-4 tw-w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
            {!details && (
              <div className="tw-flex tw-h-48 tw-items-center tw-justify-center tw-px-6 tw-text-center tw-text-sm tw-text-neutral">
                {t('importModule.fi-no-details')}
              </div>
            )}

            {details && (
              <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden">
                <Tabs
                  tabs={tabs}
                  onSelected={(tab) => {
                    const map: Record<string, TabKey> = {
                      [ActiveTab.FILE_DETAILS_IMPORT]: 'import',
                      [ActiveTab.FILE_DETAILS_LOCKS]: 'locks',
                      [ActiveTab.FILE_DETAILS_CALIBRATIONS]: 'calibrations',
                      [ActiveTab.FILE_DETAILS_FAULTS]: 'faults',
                      [ActiveTab.FILE_DETAILS_CONTROLS]: 'controls',
                      [ActiveTab.FILE_DETAILS_SIGNATURES]: 'signatures',
                      [ActiveTab.FILE_DETAILS_WARNINGS]: 'warnings',
                      [ActiveTab.FILE_DETAILS_SPEED]: 'speed',
                    }
                    setActiveTab(map[tab.type] ?? 'import')
                  }}
                >
                  <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
                    {activeTab === 'import' && <TabImport data={details} />}
                    {activeTab === 'locks' && <TabLocks data={details} />}
                    {activeTab === 'calibrations' && <TabCalibrations data={details} />}
                    {activeTab === 'faults' && <TabFaults data={details} />}
                    {activeTab === 'controls' && <TabControls data={details} />}
                    {activeTab === 'signatures' && <TabSignatures data={details} />}
                    {activeTab === 'warnings' && <TabWarnings data={details} />}
                    {activeTab === 'speed' && <TabSpeed data={details} />}
                  </div>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalLayout>
  )
}
